/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useCallback } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import Form, { Field } from '@atlaskit/form';
import SendIcon from '@atlaskit/icon/core/send';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { usePublish } from '@atlaskit/rovo-triggers';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onOpenChatButtonClick } from '../../../ai-answer-dialog/analytics';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../../controller/analytics';
import { ErrorBoundary } from '../error-boundary';

import { messages } from './messages';

type ChatFollowupInputProps = {
	query: string;
	answerString: string;
	source: 'reading-aids' | 'sain';
	experimentCohort?: string;
};

const containerStyles = xcss({
	borderRadius: '3px',
	border: `1px solid ${token('color.border')}`,
	padding: 'space.100',
});

const inputFieldContainerStyles = xcss({ flex: 1 });

const textFieldStyles = css({
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
	marginTop: token('space.0'),
	marginRight: token('space.0'),
	marginBottom: token('space.0'),
	marginLeft: token('space.0'),
	// To pull up TextField cause Field adds margin on top that can't be overridden
	marginBlockStart: token('space.negative.100'),

	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'@media (min-width: 30rem)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'& > [data-ds--text-field--input]': {
			padding: 0,
		},
	},
});

export const ChatFollowupInput = ({
	answerString,
	query,
	source,
	experimentCohort,
}: ChatFollowupInputProps) => {
	const { commonAttributes } = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const sendToChat = useCallback(
		(formData: { followupQuery: string }) => {
			if (!Boolean(formData.followupQuery)) {
				return;
			}
			fireAnalyticsEvent(onOpenChatButtonClick({ ...commonAttributes, experimentCohort }));
			fireSAINUsageEvent({ metCriteria: SainAction.OPEN_CHAT_CLICKED });

			publish({
				type: 'chat-new',
				source,
				data: {
					name: query.substring(0, 30),
					dialogues: [
						{
							human_message: { content: query },
							agent_message: { content: answerString },
						},
					],
					prompt: formData.followupQuery,
					sourceId: commonAttributes.searchSessionId,
				},
			});

			formData.followupQuery = '';
		},
		[
			query,
			answerString,
			publish,
			commonAttributes,
			fireAnalyticsEvent,
			fireSAINUsageEvent,
			source,
			experimentCohort,
		],
	);

	return (
		<ErrorBoundary fallback={<Fragment></Fragment>}>
			<Box xcss={containerStyles}>
				<Form onSubmit={sendToChat}>
					{({ formProps }) => (
						<form {...formProps}>
							<Inline grow="fill" spread="space-between" alignBlock="center" space="space.100">
								<Box xcss={inputFieldContainerStyles}>
									<Field name="followupQuery">
										{({ fieldProps }) => (
											<TextField
												autoComplete="off"
												placeholder={formatMessage(
													messages.ai_answer_component_ai_answer_ask_more_chat,
												)}
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/no-unsafe-style-overrides
												css={textFieldStyles}
												appearance="none"
												testId="ai-answer-chat-followup-input"
												{...fieldProps}
											/>
										)}
									</Field>
								</Box>
								<IconButton
									icon={SendIcon}
									label={formatMessage(messages.ai_answer_component_ai_answer_ask_more_chat_submit)}
									type="submit"
									testId="ai-answer-chat-followup-input-submit"
									spacing="compact"
								/>
							</Inline>
						</form>
					)}
				</Form>
			</Box>
		</ErrorBoundary>
	);
};
